import React, { useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { BootstrapModal } from 'shared-components/modal/bootstrap-modal';
import { AvailabilityEditor } from '@ps-design-system/components/calendar/AvailabilityEditor';
import { injectIntl, intlShape } from 'react-intl';
import CalendarAvailabilityApi from '../../../api/api-calendar-availability';
import { StatsigContext } from '../elements-statsig-provider/statsig-context';

const CAL_CONF_MODAL_LAST_SHOWN_COOKIE = 'CalConfModalLastShown_';
const HOURS_24_IN_MS = 86400000;

const CalendarAvailabilityModal = ({
  profileSSOid,
  accessToken,
  ...props
}) => {
  const {
    initialized,
    featureGates
  } = useContext(StatsigContext);

  const [showModal, setShowModal] = useState(false);
  const [dates, setDates] = useState([]);

  const canShowModal = () => {
    const lastShown = Number(PSSessions.Cookies.get(CAL_CONF_MODAL_LAST_SHOWN_COOKIE + profileSSOid));
    const hasOneDayPast = !lastShown || (HOURS_24_IN_MS <= Date.now() - lastShown);

    const isFeatureEnabled = featureGates.host_confirm_availability_modal?.() ?? false;
    return hasOneDayPast && isFeatureEnabled;
  };

  const fetchUpdateStatus = async () => {
    return CalendarAvailabilityApi.checkIfCalendarNeedsUpdate(
      profileSSOid,
      accessToken
    );
  };

  const trackModalShown = () => {
    PSSessions.Cookies.set(
      CAL_CONF_MODAL_LAST_SHOWN_COOKIE + profileSSOid,
      Date.now(),
      Date.now() + HOURS_24_IN_MS * 7
    );
  };

  const handleModalVisibility = async () => {
    if (!canShowModal()) {
      return;
    }

    const {
      confirmation_needed: confirmationNeeded,
      dates
    } = await fetchUpdateStatus();

    if (confirmationNeeded) {
      setDates(dates);
      setShowModal(true);
      trackModalShown();

      analytics.track('viewed_availability_modal', {
        ssoId: profileSSOid
      });
    }
  };

  useEffect(() => {
    if (initialized) {
      handleModalVisibility();
    }
  }, [initialized]);

  const onClose = () => {
    analytics.track('clicked_dismiss_availability_modal', {
      ssoId: profileSSOid
    });

    setShowModal(false);
  };

  const onConfirmPressed = () => {
    CalendarAvailabilityApi.confirmCalendarAvailability(profileSSOid, accessToken);

    analytics.track('clicked_confirm_availability_modal', {
      ssoId: profileSSOid
    });

    setShowModal(false);
  };

  const onViewCalendarPressed = () => {
    analytics.track('clicked_view_calendar_availability_modal', {
      ssoId: profileSSOid
    });

    window.location.href = '/calendar';
  };

  return (
    <BootstrapModal
      {...props}
      showModal={showModal}
      onClose={onClose}
      modalSize="sm-size"
      modalClass="basic-modal"
      title={''}
      body={
        <>
          <AvailabilityEditor
            intl={props.intl}
            dateBlocks={dates}
            onConfirmPressed={onConfirmPressed}
            onViewCalendarPressed={onViewCalendarPressed}
            timeZone={'UTC'}
          />
        </>
      }
    />
  );
};

CalendarAvailabilityModal.propTypes = {
  intl: intlShape.isRequired,
  profileSSOid: PropTypes.string.isRequired,
  accessToken: PropTypes.string.isRequired
};

const mapStateToProps = (state) => ({
  profileSSOid: state.profile.SSOid,
  profile: state.profile,
  accessToken: state.auth.accessToken
});

export default connect(mapStateToProps)(injectIntl(CalendarAvailabilityModal));
